package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.Modal
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.TextInputStyle
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Input

private const val MEMBERS_PATH = "/app/settings/members"

internal fun matchesWorkspaceName(
    typed: String,
    workspaceName: String,
): Boolean = workspaceName.isNotBlank() && typed.trim().equals(workspaceName.trim(), ignoreCase = true)

internal fun reauthenticationUrl(): String = "/auth/slack/start?returnTo=" + encodeUriComponent(MEMBERS_PATH)

private fun encodeUriComponent(value: String): String = js("encodeURIComponent(value)").unsafeCast<String>()

@Composable
fun TransferOwnershipModal(
    member: WorkspaceMemberDto,
    workspaceName: String,
    submitting: Boolean,
    error: String?,
    needsSignIn: Boolean,
    onConfirm: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    var typed by remember(member.userId) { mutableStateOf("") }
    val title = "Transfer ownership to ${member.displayName}?"

    Modal(
        onDismiss = onDismiss,
        widthPx = 480,
        showClose = false,
        submitting = submitting,
        ariaLabel = title,
        footer = {
            Button(label = "Cancel", variant = ButtonVariant.Ghost, enabled = !submitting, onClick = onDismiss)
            if (needsSignIn) {
                Button(
                    label = "Sign in again",
                    variant = ButtonVariant.Primary,
                    onClick = { window.location.href = reauthenticationUrl() },
                )
            } else {
                Button(
                    label = if (submitting) "Transferring…" else "Transfer ownership",
                    variant = ButtonVariant.Danger,
                    enabled = !submitting && matchesWorkspaceName(typed, workspaceName),
                    onClick = { onConfirm(typed) },
                )
            }
        },
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.lg)) {
            SpanText(
                text = title,
                modifier = Modifier
                    .fontSize(Typography.h2.size)
                    .fontWeight(Typography.h2.weight)
                    .color(Palette.status(StatusHue.Danger).solid)
                    .margin(bottom = Spacing.xs),
            )
            SpanText(
                text = MemberDialogCopy.transfer(member, workspaceName),
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .color(Palette.textSecondary),
            )
        }
        if (needsSignIn) {
            SpanText(
                text = "For your security, sign in again before transferring ownership. You'll come back to this page.",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textPrimary)
                    .margin(bottom = Spacing.md),
            )
        } else {
            Column(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.lg)) {
                SpanText(
                    text = "Type \"$workspaceName\" to confirm",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textSecondary)
                        .margin(bottom = Spacing.xxs),
                )
                Input(
                    type = InputType.Text,
                    attrs = TextInputStyle.toModifier()
                        .fillMaxWidth()
                        .toAttrs {
                            placeholder(workspaceName)
                            value(typed)
                            onInput { typed = it.value }
                        },
                )
            }
        }
        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.status(StatusHue.Danger).solid),
            )
        }
    }
}
